import { CreateOrg } from "@garther/interfaces";
import { toast, useI18n } from "@garther/ui/libs";
import { useForm } from "@tanstack/react-form";
import { createFileRoute, useNavigate, useRouteContext } from "@tanstack/react-router";

import { useUpdateOrg } from "@/features/org/hooks/use-org-query";

import { OrgForm } from "../../-components/org-form";

export const Route = createFileRoute("/_auth/org/$orgId/edit/")({
  component: RouteComponent
});

function RouteComponent() {
  const { t } = useI18n();
  const { org } = useRouteContext({ from: "/_auth/org/$orgId/edit/" });

  const navigate = useNavigate();

  const updateOrgMutation = useUpdateOrg({
    onSuccess: () => {
      toast.success(t("org.edit.success"));
      navigate({ to: "/org/$orgId", params: { orgId: org.id } });
    }
  });

  const form = useForm<CreateOrg>({
    defaultValues: {
      name: org.name,
      tagline: org.tagline,
      description: org.description,
      mission: org.mission,
      avatar: org.avatar,
      cover: org.cover,
      contact: {
        email: org.contact?.email || null,
        phone: org.contact?.phone || null,
        website: org.contact?.website || null,
        instagram: org.contact?.instagram || null,
        facebook: org.contact?.facebook || null,
        twitter: org.contact?.twitter || null,
        youtube: org.contact?.youtube || null
      }
    },
    onSubmit: async ({ value }) => {
      updateOrgMutation.mutate({
        orgId: org.id,
        data: {
          name: value.name?.trim(),
          tagline: value.tagline?.trim(),
          description: value.description?.trim(),
          mission: value.mission?.trim(),
          avatar: value.avatar?.trim(),
          cover: value.cover?.trim(),
          contact: value.contact ?? undefined
        }
      });
    }
  });

  const onCancel = () => {
    navigate({ to: "/org/$orgId", params: { orgId: org.id } });
  };

  return (
    <div className="py-4 md:py-6 lg:py-8 px-0 sm:px-2 md:px-4 lg:px-8 space-y-6">
      <div className="space-y-4 sm:space-y-6 max-w-4xl mx-auto">
        <div className="px-4 md:px-6">
          <p className="text-responsive-2xl font-light text-text-primary mb-1 font-literata uppercase">
            {t("org.edit.title")}
          </p>
          <p className="text-responsive-base font-normal text-text-secondary">
            {t("org.edit.subtitle", { orgName: org.name })}
          </p>
        </div>

        <OrgForm form={form} isSubmitting={updateOrgMutation.isPending} onCancel={onCancel} />
      </div>
    </div>
  );
}
